// The product editor: a catalogue product, or one placed in the drawing, edited
// in place in its modal — the part tree, each node's fields, its drawing, a new
// product from the selection or from a group, and its save. docs/claude/products.md.
import { unreachable } from '../../shared/exhaustive';
import {
  KNOWN_CATEGORIES,
  traitsOf,
} from '../../shared/categories';
import {
  dictDefaults,
  missingNames,
  typedNames,
} from '../../shared/names';
import {
  type GeomEntity,
  type Geometry,
  type Polygon,
  type ProductCategory,
  type ProductInstance,
  type ProductTemplate,
  type TextI18n,
  type Vec2,
} from '../../shared/types';
import {
  postUpdateFixups,
  prepId,
  productGroupOf,
  productParts,
  reconcileGroupFromTemplate,
} from './catalogSync';
import {
  partOfCopy,
  productOfCopy,
  refreshCopy,
} from '../catalogueCopy';
import { normalizeCeiling } from '../ceiling';
import {
  invalidate,
  pushHistory,
  renderProperties,
  state,
} from './core';
import { curveTessellation } from '../curves';
import { parseDxf } from '../dxf';
import { isElecRoom } from '../elecSchematic';
import {
  boundsSize,
  centerGeometry,
  computeBounds,
  emptyBounds,
  expandBounds,
  instanceToWorld,
  isFiniteBounds,
  rotatePoint,
} from '../geometry';
import { groupById } from '../groups';
import {
  tr,
  trIn,
} from '../i18n';
import { partConnection } from '../multipart';
import {
  baseOfI18n,
  cleanI18n,
  i18nInputs,
  seedI18n,
} from './nameFields';
import { renderPdfToBackground } from '../pdf';
import { platform } from '../platform';
import {
  addableFields,
  addField,
  addPart,
  fieldOptions,
  type FieldSpec,
  finishEdit,
  type ItemKind as FieldItemKind,
  type ListedField,
  listFields,
  lockedField,
  newItem,
  propValue,
  removable,
  removeField,
  removePart,
  SECTIONS,
} from '../productFields';
import { instanceCorners } from '../render';
import { toHexInput } from './standards';
import {
  formatPower,
  parsePower,
  unlinkProductGroup,
} from '../variants';
import {
  $,
  canEditStandards,
  deselectAll,
  ellipseParams,
  instanceOutline,
  loadProducts,
  normalizeColor,
  setTool,
  storedHashes,
  syncDimensions,
} from '../main';
import {
  evalFormula,
  parseMeasure,
  savedColorSwatches,
} from './panelRows';
import { editPartDrawing } from './drawingEditor';
import {
  armed,
  type ArmedPick,
  armPick,
  endArmed,
} from './armedPick';

/** The product the editor edits in place — a deep copy of the stored one, or a
 *  new one — and the node it shows: the product, or one of its parts. Every
 *  control writes one field of it, and Save writes it back whole
 *  (docs/claude/products.md, *Editing a product*). */
export let npDraft: ProductTemplate | null = null;
let npNode: 'product' | number = 'product';
/** The stored version a catalogue edit began from (`storedHashes`): its save is
 *  refused once someone else has saved over it. */
let npBase: string | null = null;

/** What carries a drawing: a single-body product, or a part. */
export type DrawingHolder = Pick<ProductTemplate, 'dxf' | 'fileName' | 'geometry' | 'imageDataUrl'> & { width: number; depth: number; name?: string };
type FieldOwner = Record<string, unknown>;

const deepCopy = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** A new product as the editor starts it. */
function newDraft(category: ProductCategory, extra: Partial<ProductTemplate> = {}): ProductTemplate {
  return {
    id: `user-${Date.now()}`, name: '', category, width: 600, depth: 600, color: traitsOf(category).color,
    properties: [{ key: 'power', label: 'Power', value: '' }], builtin: false, ...extra,
  };
}

/** The node the editor shows, and what kind of object it is. */
function npNodeObject(): { obj: FieldOwner; kind: FieldItemKind } | null {
  if (!npDraft) return null;
  if (npNode === 'product') return { obj: npDraft as unknown as FieldOwner, kind: 'product' };
  const part = npDraft.parts?.[npNode];
  return part ? { obj: part as unknown as FieldOwner, kind: 'part' } : null;
}

/** The editor's category list: every category the app knows, by its own name,
 *  then each one the catalogue has beyond them and `own` — so a product never
 *  opens with its category blanked (a select set to a value it has no option for
 *  reads ''), which saved it uncategorised and out of every catalogue group. */
function categorySelect(own: string, onChange: (v: string) => void): HTMLSelectElement {
  const sel = document.createElement('select');
  const listed = new Set<string>();
  const add = (value: string, label: string): void => {
    if (!value || listed.has(value)) return;
    listed.add(value);
    const o = document.createElement('option');
    o.value = value;
    o.textContent = label;
    sel.appendChild(o);
  };
  for (const c of KNOWN_CATEGORIES) { const name = traitsOf(c).name; if (name) add(c, tr(name)); }
  for (const c of [own, ...state.templates.map((t) => t.category)]) add(c, c);
  sel.value = own || 'custom';
  sel.addEventListener('change', () => onChange(sel.value));
  return sel;
}

/** Natural pixel size of an image data URL (for keeping a part's aspect ratio). */
export function imageSize(dataUrl: string): Promise<{ w: number; h: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve({ w: 0, h: 0 });
    img.src = dataUrl;
  });
}
export function openProductModal(edit?: ProductTemplate, instance?: ProductInstance): void {
  $('np-error').textContent = '';
  npInstanceId = instance?.id ?? null;
  // Read-only users (no `edit` claim) can open the window to view a product but
  // not save it — hide the Save button. That holds for a PER-PART edit too: the
  // claim governs who may change a product's spec at all, not merely where the
  // result is stored.
  const npReadOnly = !canEditStandards();
  $('np-save').style.display = npReadOnly ? 'none' : '';
  $('np-save').textContent = instance ? tr('Apply to this part') : edit ? tr('Save') : tr('Create Product');
  // Says whose copy is being changed, so the window can't be mistaken for the
  // catalogue one it is otherwise identical to. Pointless with no Save button.
  const scoped = !!instance && !npReadOnly;
  $('np-scope').textContent = !scoped ? ''
    : productGroupOf(instance!)
      ? tr('Changes apply to this placed product only, which is then no longer linked to the catalogue.')
      : tr('Changes apply to this placed part only — the catalogue is not updated.');
  $('np-scope').classList.toggle('hidden', !scoped);
  npBase = edit && !instance ? storedHashes.get(edit.id) ?? null : null;
  npDraft = edit ? deepCopy(edit) : newDraft('shower');
  npNode = 'product';
  // Written as separate tr() calls, not tr(cond ? … : …): the i18n gate reads
  // the source text and only sees a literal that IS the argument.
  $('np-title').textContent = !edit ? tr('New Product')
    : instance
      ? (npReadOnly ? tr('View Part') : tr('Edit Part'))
      : (npReadOnly ? tr('View Product') : tr('Edit Product'));
  renderEditor();
  $('modal-backdrop').classList.remove('hidden');
}
/** Map an entity's coordinate points through `tp` (rotating angle/vector fields
 *  by `rot` radians), e.g. to transform instance-local geometry into world. */
function transformEntity(e: GeomEntity, tp: (p: Vec2) => Vec2, rot: number): GeomEntity {
  switch (e.kind) {
    case 'line': return { ...e, a: tp(e.a), b: tp(e.b) };
    case 'polyline': return { ...e, points: e.points.map(tp) };
    case 'circle': return { ...e, center: tp(e.center) };
    case 'arc': return { ...e, center: tp(e.center), startAngle: e.startAngle + rot, endAngle: e.endAngle + rot };
    case 'ellipse': return { ...e, center: tp(e.center), major: rotatePoint(e.major, (rot * 180) / Math.PI) };
    case 'spline': return { ...e, control: e.control.map(tp) };
    case 'text': return { ...e, pos: tp(e.pos) };
    case 'hatch': return { ...e, loops: e.loops.map((l: Vec2[]) => l.map(tp)) };
  }
}

/**
 * Build a new product whose first part's drawing is the selected instances'
 * geometry (in place of an imported DXF). Each instance contributes its vector
 * geometry — or its footprint outline when it has none — transformed to world
 * and re-centred on the selection, then the new-product modal opens prefilled.
 */
export function newProductFromSelection(): void {
  const insts = state.drawing.instances.filter((i) => state.selectedIds.includes(i.id));
  if (insts.length < 1) return;
  // Centre on the selection's footprint bounding box.
  let b = emptyBounds();
  for (const inst of insts) for (const c of instanceOutline(inst)) expandBounds(b, c);
  if (!isFiniteBounds(b)) return;
  const cx = (b.min.x + b.max.x) / 2, cy = (b.min.y + b.max.y) / 2;
  const recentre = (p: Vec2): Vec2 => ({ x: p.x - cx, y: p.y - cy });
  const entities: GeomEntity[] = [];
  for (const inst of insts) {
    const rot = ((inst.rotation || 0) * Math.PI) / 180;
    const toLocal = (p: Vec2): Vec2 => recentre(instanceToWorld(inst, p));
    if (inst.geometry && inst.geometry.entities.length) {
      for (const e of inst.geometry.entities) entities.push(transformEntity(e, toLocal, rot));
    } else {
      // No vector geometry (room / image part): use the footprint outline.
      const pts = instanceCorners(inst).map(recentre);
      entities.push({ kind: 'polyline', points: pts, closed: true });
    }
  }
  const geometry: Geometry = { entities, bounds: computeBounds(entities) };

  // Open the New Product modal on a product whose drawing is this geometry.
  openProductModal();
  npDraft = newDraft('custom', {
    width: Math.max(1, Math.round(b.max.x - b.min.x)),
    depth: Math.max(1, Math.round(b.max.y - b.min.y)),
    geometry,
    fileName: 'selection',
  });
  $('np-title').textContent = tr('New Product from selection');
  renderEditor();
}

/** The placed part being edited, when the editor was opened on ONE copy in the
 *  drawing rather than on the catalog entry. Save then writes to that copy (and,
 *  for a multi-part product, to its group) and never to the catalog. */
let npInstanceId: string | null = null;
export function closeProductModal(): void {
  npLinkGroupId = null;
  npDraft = null;
  $('modal-backdrop').classList.add('hidden');
  npInstanceId = null;
}

/** A small preview of a drawing: the raster image, the vector geometry, or an
 *  empty placeholder. */
function partThumbnail(holder: DrawingHolder): HTMLElement {
  const box = document.createElement('div');
  box.className = 'np-part-thumb';
  if (holder.imageDataUrl) {
    const img = document.createElement('img');
    img.src = holder.imageDataUrl;
    img.alt = holder.fileName || 'drawing';
    box.appendChild(img);
  } else if (holder.geometry && holder.geometry.entities.length) {
    const cv = document.createElement('canvas');
    cv.width = 140;
    cv.height = 140;
    drawGeometryThumb(cv, holder.geometry);
    box.appendChild(cv);
    box.classList.add('editable');
    box.title = tr('Double-click to edit this drawing');
    box.style.cursor = 'pointer';
    box.addEventListener('dblclick', () => editPartDrawing(holder));
  } else {
    box.classList.add('empty');
    box.textContent = tr('No drawing');
  }
  return box;
}

/** Render a vector Geometry fit inside a small canvas, for the part thumbnail. */
/** A #rrggbb colour with an alpha (0..1), as an #rrggbbaa string the canvas accepts. */
export function withAlpha(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255).toString(16).padStart(2, '0');
  return `#${m[1]}${m[2]}${m[3]}${a}`;
}

function drawGeometryThumb(canvas: HTMLCanvasElement, geom: Geometry): void {
  const ctx = canvas.getContext('2d');
  if (!ctx || !isFiniteBounds(geom.bounds)) return;
  const W = canvas.width, H = canvas.height, m = 10;
  const sz = boundsSize(geom.bounds);
  const scale = Math.min((W - 2 * m) / (sz.x || 1), (H - 2 * m) / (sz.y || 1));
  const cx = (geom.bounds.min.x + geom.bounds.max.x) / 2;
  const cy = (geom.bounds.min.y + geom.bounds.max.y) / 2;
  const S = (x: number, y: number) => ({ x: (x - cx) * scale + W / 2, y: -(y - cy) * scale + H / 2 });
  ctx.clearRect(0, 0, W, H);
  ctx.lineWidth = 1;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  for (const e of geom.entities) {
    // Colour-tinted entities (e.g. from drawn polygons) keep their own colour;
    // untinted imported geometry uses the default light stroke.
    ctx.strokeStyle = e.color || '#cdd6e0';
    // A hatch paints its loops with the entity's fill colour (even-odd holes).
    if (e.kind === 'hatch') {
      ctx.beginPath();
      for (const loop of e.loops) { loop.forEach((p, k) => { const s = S(p.x, p.y); k ? ctx.lineTo(s.x, s.y) : ctx.moveTo(s.x, s.y); }); ctx.closePath(); }
      ctx.fillStyle = withAlpha(e.color || '#9fb3c8', 0.35);
      ctx.fill('evenodd');
      continue;
    }
    ctx.beginPath();
    if (e.kind === 'line') { const a = S(e.a.x, e.a.y), z = S(e.b.x, e.b.y); ctx.moveTo(a.x, a.y); ctx.lineTo(z.x, z.y); }
    else if (e.kind === 'polyline') { e.points.forEach((p, k) => { const s = S(p.x, p.y); k ? ctx.lineTo(s.x, s.y) : ctx.moveTo(s.x, s.y); }); if (e.closed) ctx.closePath(); }
    else if (e.kind === 'circle') { const c = S(e.center.x, e.center.y); ctx.arc(c.x, c.y, e.radius * scale, 0, Math.PI * 2); }
    else if (e.kind === 'arc') { let sw = e.endAngle - e.startAngle; while (sw <= 0) sw += 360; const sg = Math.max(6, Math.ceil(sw / 6)); for (let k = 0; k <= sg; k++) { const a = (e.startAngle + sw * k / sg) * Math.PI / 180; const s = S(e.center.x + e.radius * Math.cos(a), e.center.y + e.radius * Math.sin(a)); k ? ctx.lineTo(s.x, s.y) : ctx.moveTo(s.x, s.y); } }
    else if (e.kind === 'ellipse' || e.kind === 'spline') { const pts = curveTessellation(e); if (!pts) continue; pts.forEach((p, k) => { const s = S(p.x, p.y); k ? ctx.lineTo(s.x, s.y) : ctx.moveTo(s.x, s.y); }); if (e.closed) ctx.closePath(); }
    else continue;
    ctx.stroke();
  }
}

/** Redraw the editor: the tree and the shown node's fields. */
export function renderEditor(): void {
  if (!npDraft) return;
  if (typeof npNode === 'number' && !npDraft.parts?.[npNode]) npNode = 'product';
  renderPartTree();
  renderNodeFields();
}

/** The tree: the product, then each of its parts; click one to list its fields. */
function renderPartTree(): void {
  const host = $('np-part-tree');
  host.innerHTML = '';
  if (!npDraft) return;
  const parts = npDraft.parts ?? [];
  const row = (label: string, node: 'product' | number, badge: string, del?: () => void): void => {
    const r = document.createElement('div');
    r.className = 'np-tree-row' + (node === npNode ? ' active' : '');
    r.dataset.node = String(node);
    const b = document.createElement('span');
    b.className = 'conn-badge';
    b.textContent = badge;
    const name = document.createElement('span');
    name.className = 'np-tree-name';
    name.textContent = label;
    r.append(b, name);
    if (del) {
      const x = document.createElement('button');
      x.className = 'del mini';
      x.textContent = '✕';
      x.title = tr('Delete part');
      x.disabled = parts.length <= 1;
      x.addEventListener('click', (e) => { e.stopPropagation(); del(); });
      r.appendChild(x);
    }
    r.addEventListener('click', () => { npNode = node; renderEditor(); });
    host.appendChild(r);
  };
  row(npDraft.name || tr('Product'), 'product', '▣');
  parts.forEach((p, i) => row(p.name || `${tr('Part')} ${i + 1}`, i, String(i + 1), () => {
    removePart(npDraft!, i);
    npNode = 'product';
    renderEditor();
  }));
}

/** The shown node's fields. */
function renderNodeFields(): void {
  const host = $('np-part-detail');
  host.innerHTML = '';
  const node = npNodeObject();
  if (!node) return;
  // A drawing is imported, not typed: its control shows on whatever can carry
  // one, with or without one yet.
  const holds = node.kind === 'part' || !npDraft!.parts?.length;
  renderFields(host, node.obj, node.kind, node.obj, holds);
}

/** An object's fields in the editor's order, each with its control and (unless
 *  the object cannot be without it) its remove button, then "Add field…". The
 *  product's and a part's are headed by section. `owner` is the product or part
 *  a nested list belongs to — what a port's position is measured on. */
function renderFields(host: HTMLElement, obj: FieldOwner, kind: FieldItemKind, owner: FieldOwner, holdsDrawing = false): void {
  const headed = kind === 'product' || kind === 'part';
  let section = '';
  const head = (s: string): void => {
    if (!headed || s === section) return;
    host.appendChild(npDetailHeading(s));
    section = s;
  };
  const fields = listFields(obj, kind);
  if (holdsDrawing && !fields.some((f) => f.spec?.kind === 'drawing')) {
    const at = fields.findIndex((f) => SECTIONS.indexOf(f.section) > SECTIONS.indexOf('Drawing'));
    fields.splice(at < 0 ? fields.length : at, 0, { key: 'geometry', spec: null, section: 'Drawing' });
  }
  for (const f of fields) {
    head(f.section);
    if (f.key === 'geometry' && kind !== 'property') { host.appendChild(fieldBlock(tr('Drawing'), drawingControl(obj as unknown as DrawingHolder))); continue; }
    host.appendChild(fieldRow(obj, f, kind, owner));
  }
  const more = addableFields(obj, kind);
  if (more.length) host.appendChild(addFieldMenu(obj, kind, more));
}

/** A labelled control that takes the row's whole width. */
function fieldBlock(label: string, control: HTMLElement): HTMLElement {
  const row = document.createElement('div');
  row.className = 'np-field np-field-block';
  const lab = document.createElement('label');
  lab.className = 'np-field-label';
  lab.textContent = label;
  row.append(lab, control);
  return row;
}

/** One field: its label, its control, and its remove button when it may go. */
function fieldRow(obj: FieldOwner, f: ListedField, kind: FieldItemKind, owner: FieldOwner): HTMLElement {
  const spec = f.spec;
  const value = obj[f.key];
  const control = spec ? fieldControl(obj, f.key, spec, owner) : genericControl(value, (v) => { obj[f.key] = v; });
  const block = spec ? spec.kind === 'list' || spec.kind === 'object' || spec.kind === 'i18n' || spec.kind === 'longText' : typeof value === 'object' && value !== null;
  const row = document.createElement('div');
  row.className = block ? 'np-field np-field-block' : 'np-field';
  row.dataset.field = f.key;
  const lab = document.createElement('label');
  lab.className = 'np-field-label';
  lab.textContent = spec ? tr(spec.labelOf?.(obj) ?? spec.label) : f.key;
  // A list under a heading of its own name ("Properties") needs no second label.
  lab.hidden = !!spec && spec.kind === 'list' && spec.label === spec.section;
  row.append(lab, control);
  const lock = lockedField(obj, f.key, kind);
  if (lock) lockFieldControl(row, control, lock);
  if (removable(f.key, kind, obj)) {
    const del = document.createElement('button');
    del.className = 'del mini np-field-remove';
    del.textContent = '✕';
    del.title = tr('Remove this field');
    del.addEventListener('click', () => { removeField(obj, f.key, kind); renderEditor(); });
    row.appendChild(del);
  }
  return row;
}

/** A field shown but not changed (`lockedField`): its controls greyed out, its
 *  add and remove buttons gone, and the reason under it. */
function lockFieldControl(row: HTMLElement, control: HTMLElement, reason: string): void {
  for (const b of Array.from(control.querySelectorAll('button'))) b.remove();
  for (const el of Array.from(control.querySelectorAll('input, select, textarea')) as Array<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) el.disabled = true;
  row.classList.add('np-field-locked');
  const note = document.createElement('div');
  note.className = 'field-hint np-locked-note';
  note.textContent = tr(reason);
  row.appendChild(note);
}

/** A text input that writes as it is typed. */
function textInput(value: unknown, set: (v: string) => void, multiline = false): HTMLInputElement | HTMLTextAreaElement {
  const el = multiline ? document.createElement('textarea') : document.createElement('input');
  if (el instanceof HTMLInputElement) el.type = 'text';
  else el.rows = 3;
  el.value = value == null ? '' : String(value);
  el.addEventListener('input', () => set(el.value));
  return el;
}

/** A number input taking a formula, or a length with a unit (`parseMeasure`). */
function numberInput(value: unknown, set: (v: number) => void, length: boolean): HTMLInputElement {
  const el = document.createElement('input');
  el.type = 'text';
  el.inputMode = 'decimal';
  el.value = value == null ? '' : String(value);
  el.addEventListener('change', () => {
    const n = length ? parseMeasure(el.value, 'mm') : evalFormula(el.value);
    if (n !== null && Number.isFinite(n)) { set(n); el.value = String(Math.round(n * 1000) / 1000); }
  });
  el.addEventListener('keydown', (e) => { if (e.key === 'Enter') el.blur(); });
  return el;
}

/** A power in watts, shown in kW from 1000 W up; typed as "36 kW", "750 W" or a
 *  bare number of watts (`parsePower`). */
function powerInput(value: unknown, set: (v: number) => void): HTMLInputElement {
  const el = document.createElement('input');
  el.type = 'text';
  el.inputMode = 'decimal';
  const show = (v: unknown): string => (typeof v === 'number' && Number.isFinite(v) ? formatPower(v) : '');
  let cur = value;
  el.value = show(cur);
  el.title = tr('Watts, or kW: 36 kW, 750 W, 36000');
  el.addEventListener('change', () => {
    const w = parsePower(el.value);
    if (w !== null) { set(w); cur = w; }
    el.value = show(cur);
  });
  el.addEventListener('keydown', (e) => { if (e.key === 'Enter') el.blur(); });
  return el;
}

/** A select whose current value is always one of its options, even one the
 *  list does not know — so opening never changes it. */
function optionSelect(value: unknown, options: ReadonlyArray<{ value: string; label: string }>, set: (v: string) => void): HTMLSelectElement {
  const sel = document.createElement('select');
  const cur = value == null ? '' : String(value);
  const all = options.some((o) => o.value === cur) || !cur ? options : [...options, { value: cur, label: cur }];
  for (const o of all) {
    const opt = document.createElement('option');
    opt.value = o.value;
    opt.textContent = o.label === o.value ? o.label : tr(o.label);
    sel.appendChild(opt);
  }
  sel.value = cur;
  sel.addEventListener('change', () => set(sel.value));
  return sel;
}

/** A product's or a part's names: one row per language, each required
 *  (docs/claude/names.md §4). `name` mirrors the English, else the first one
 *  filled, for older readers; a name with no English beside it is shown as the
 *  English, as the names fall back to it. */
function catalogueNameRows(obj: FieldOwner): HTMLElement {
  const map: TextI18n = { ...(obj.nameI18n as TextI18n | undefined) };
  const name = String(obj.name ?? '').trim();
  if (!(map.en ?? '').trim() && name) map.en = name;
  return i18nInputs(map, {
    onChange: () => {
      const clean = cleanI18n(map);
      if (clean) obj.nameI18n = clean; else delete obj.nameI18n;
      obj.name = clean ? clean.en ?? clean.it ?? clean.fr ?? '' : '';
      renderPartTree();
    },
  });
}

/** Whether the editor is on a room (`isElecRoom`): a room product, or a placed
 *  room's own edit, whose product read back has no `autoBenches`. It is offered
 *  no electrical preparation (`fieldOptions`, `newItem`). */
function npRoom(): boolean {
  const inst = npInstanceId ? state.drawing.instances.find((i) => i.id === npInstanceId) : undefined;
  return (!!npDraft && isElecRoom(npDraft)) || (!!inst && isElecRoom(inst));
}

/** A known field's own control, writing that field alone. */
function fieldControl(obj: FieldOwner, key: string, spec: FieldSpec, owner: FieldOwner): HTMLElement {
  const set = (v: unknown): void => { obj[key] = v; };
  switch (spec.kind) {
    case 'text': return textInput(obj[key], (v) => { set(v); if (key === 'name') renderPartTree(); });
    case 'longText': return textInput(obj[key], set, true);
    case 'mm':
    case 'signedMm': return numberInput(obj[key], set, true);
    case 'number': return numberInput(obj[key], set, false);
    case 'propValue': return textInput(obj[key], (v) => set(propValue(v)));
    case 'power': return powerInput(obj[key], set);
    case 'bool': {
      const el = document.createElement('input');
      el.type = 'checkbox';
      el.checked = !!obj[key];
      el.addEventListener('change', () => set(el.checked));
      return el;
    }
    case 'color': {
      const input = document.createElement('input');
      input.type = 'color';
      input.value = toHexInput(String(obj[key] ?? '#cdd6e0'));
      const apply = (raw: string): void => {
        const n = normalizeColor(raw);
        if (!n) return;
        input.value = toHexInput(n);
        set(n);
      };
      input.addEventListener('input', () => apply(input.value));
      const wrap = document.createElement('div');
      wrap.className = 'np-color';
      wrap.append(input, savedColorSwatches(apply));
      return wrap;
    }
    case 'select': return optionSelect(obj[key], fieldOptions(spec, obj, npRoom()), (v) => { set(v); if (key === 'lightKind') renderEditor(); });
    case 'category': return categorySelect(String(obj[key] ?? ''), set);
    case 'i18n': {
      if (key === 'name') return catalogueNameRows(obj);
      const companion = spec.companions?.[0] ?? `${key}I18n`;
      const base = String(obj[key] ?? '');
      const map = seedI18n(base, obj[companion] as TextI18n | undefined);
      // The text follows its translations only where it is what they give. One
      // of its own (an English name beside Italian and French ones) is shown on
      // its own row and kept, whatever the translations become.
      const own = base !== baseOfI18n(map);
      const box = document.createElement('div');
      box.className = 'i18n-inputs';
      if (own) {
        const row = document.createElement('div');
        row.className = 'i18n-row np-i18n-base';
        const tag = document.createElement('span');
        tag.className = 'i18n-tag';
        tag.textContent = tr('Base');
        row.append(tag, textInput(base, (v) => { set(v); if (key === 'name') renderPartTree(); }));
        box.appendChild(row);
      }
      box.appendChild(i18nInputs(map, {
        onChange: () => {
          const clean = cleanI18n(map);
          if (clean) obj[companion] = clean; else delete obj[companion];
          if (!own) set(baseOfI18n(map));
          if (key === 'name') renderPartTree();
        },
      }));
      return box;
    }
    case 'drawing': return drawingControl(obj as unknown as DrawingHolder);
    case 'mmList': {
      const el = document.createElement('input');
      el.type = 'text';
      el.value = Array.isArray(obj[key]) ? (obj[key] as number[]).join(', ') : '';
      el.addEventListener('change', () => {
        const parts = el.value.split(/[;,]/).map((s) => s.trim()).filter(Boolean);
        const nums = parts.map((s) => parseMeasure(s, 'mm'));
        if (nums.every((n): n is number => n !== null && Number.isFinite(n))) set(nums);
        el.value = Array.isArray(obj[key]) ? (obj[key] as number[]).join(', ') : '';
      });
      return el;
    }
    case 'list': return listControl(obj, key, spec.item ?? 'property', owner);
    case 'object': {
      const box = document.createElement('div');
      box.className = 'np-object';
      const inner = obj[key];
      if (inner && typeof inner === 'object') renderFields(box, inner as FieldOwner, spec.item ?? 'gap', owner);
      return box;
    }
    case 'partRef': {
      const parts = npDraft?.parts ?? [];
      return optionSelect(obj[key], parts.map((p, i) => ({ value: p.id, label: p.name || `${tr('Part')} ${i + 1}` })), set);
    }
    case 'portX':
    case 'portY': {
      const size = Number(spec.kind === 'portX' ? owner.width : owner.depth) || 0;
      const n = Number(obj[key]) || 0;
      return numberInput(Math.round(n * size / 2), (mmv) => set(size ? mmv / (size / 2) : 0), true);
    }
  }
  return unreachable(spec.kind, 'field kind');
}

/** A field this build does not know, drawn by its value's type: a number, text
 *  or checkbox; an object's keys each with their own control; a list with add
 *  and remove. */
function genericControl(value: unknown, set: (v: unknown) => void): HTMLElement {
  if (typeof value === 'number') return numberInput(value, set, false);
  if (typeof value === 'boolean') {
    const el = document.createElement('input');
    el.type = 'checkbox';
    el.checked = value;
    el.addEventListener('change', () => set(el.checked));
    return el;
  }
  if (Array.isArray(value)) {
    const box = document.createElement('div');
    box.className = 'np-list';
    value.forEach((item, i) => {
      const row = document.createElement('div');
      row.className = 'np-list-item';
      const del = document.createElement('button');
      del.className = 'del mini';
      del.textContent = '✕';
      del.title = tr('Remove');
      del.addEventListener('click', () => { value.splice(i, 1); renderEditor(); });
      row.append(genericControl(item, (v) => { value[i] = v; }), del);
      box.appendChild(row);
    });
    const add = document.createElement('button');
    add.className = 'mini';
    add.textContent = tr('＋ Add');
    const last = value[value.length - 1];
    add.addEventListener('click', () => {
      value.push(typeof last === 'number' ? 0 : typeof last === 'boolean' ? false : last && typeof last === 'object' ? {} : '');
      renderEditor();
    });
    box.appendChild(add);
    return box;
  }
  if (value && typeof value === 'object') {
    const o = value as FieldOwner;
    const box = document.createElement('div');
    box.className = 'np-object';
    for (const k of Object.keys(o)) {
      const row = document.createElement('div');
      row.className = 'np-field';
      const lab = document.createElement('label');
      lab.className = 'np-field-label';
      lab.textContent = k;
      const del = document.createElement('button');
      del.className = 'del mini np-field-remove';
      del.textContent = '✕';
      del.title = tr('Remove this field');
      del.addEventListener('click', () => { delete o[k]; renderEditor(); });
      row.append(lab, genericControl(o[k], (v) => { o[k] = v; }), del);
      box.appendChild(row);
    }
    return box;
  }
  return textInput(value, set);
}

/** A list field: each item with its own fields and a remove button, then "Add". */
function listControl(obj: FieldOwner, key: string, item: FieldItemKind, owner: FieldOwner): HTMLElement {
  const box = document.createElement('div');
  box.className = 'np-list';
  const items = Array.isArray(obj[key]) ? (obj[key] as FieldOwner[]) : [];
  items.forEach((it, i) => {
    const row = document.createElement('div');
    row.className = 'np-list-item';
    row.dataset.item = item;
    const fields = document.createElement('div');
    fields.className = 'np-list-fields';
    if (it && typeof it === 'object') renderFields(fields, it, item, owner);
    else fields.appendChild(genericControl(it, (v) => { items[i] = v as FieldOwner; }));
    const del = document.createElement('button');
    del.className = 'del mini';
    del.textContent = '✕';
    del.title = tr('Remove');
    del.addEventListener('click', () => { items.splice(i, 1); renderEditor(); });
    row.append(fields, del);
    box.appendChild(row);
  });
  const add = document.createElement('button');
  add.className = 'mini np-list-add';
  add.textContent = tr('＋ Add');
  add.addEventListener('click', () => {
    if (!Array.isArray(obj[key])) obj[key] = [];
    (obj[key] as FieldOwner[]).push(newItem(item, items, npDraft!, npRoom()));
    renderEditor();
  });
  box.appendChild(add);
  return box;
}

/** "Add field…": the known fields the object lacks. */
function addFieldMenu(obj: FieldOwner, kind: FieldItemKind, more: Array<{ key: string; spec: FieldSpec }>): HTMLElement {
  const sel = document.createElement('select');
  sel.className = 'np-add-field';
  const first = document.createElement('option');
  first.value = '';
  first.textContent = tr('Add field…');
  sel.appendChild(first);
  for (const m of more) {
    const o = document.createElement('option');
    o.value = m.key;
    o.textContent = tr(m.spec.label);
    sel.appendChild(o);
  }
  sel.addEventListener('change', () => {
    if (!sel.value) return;
    addField(obj, sel.value, kind);
    renderEditor();
  });
  return sel;
}

/** The drawing control: its preview, import a file or the selected polygons, or
 *  clear it so the part falls back to its dynamic drawing. */
function drawingControl(holder: DrawingHolder): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'np-drawing';
  wrap.appendChild(partThumbnail(holder));
  const buttons = document.createElement('div');
  buttons.className = 'np-drawing-buttons';
  const pick = document.createElement('button');
  pick.className = 'mini np-part-pick';
  pick.textContent = holder.fileName ? holder.fileName : tr('Choose DXF, PDF, PNG or JPG…');
  pick.title = tr('Import a vector (DXF/DWG) or raster (PDF/PNG/JPG) drawing for this part');
  pick.addEventListener('click', () => void pickNodeDrawing(holder));
  const poly = document.createElement('button');
  poly.className = 'mini np-part-pick';
  poly.textContent = tr('◇ Use selected polygons');
  poly.title = tr("Use the polygons currently selected in the drawing as this part's symbol");
  poly.addEventListener('click', () => usePartPolygons(holder));
  buttons.append(pick, poly);
  if (holder.geometry || holder.imageDataUrl || holder.dxf || holder.fileName) {
    const clear = document.createElement('button');
    clear.className = 'mini np-part-pick';
    clear.textContent = tr('✕ Clear loaded drawing');
    clear.title = tr('Remove the imported drawing/image so the part uses its dynamic drawing');
    clear.addEventListener('click', () => {
      delete holder.geometry;
      delete holder.dxf;
      delete holder.imageDataUrl;
      delete holder.fileName;
      renderEditor();
    });
    buttons.appendChild(clear);
  }
  wrap.appendChild(buttons);
  return wrap;
}

/** The bottom-left tree listing every part; click one to edit it in the centre. */

/** A small sub-heading in the part-detail column. */
function npDetailHeading(text: string): HTMLElement {
  const h = document.createElement('div');
  h.className = 'np-detail-head';
  h.textContent = tr(text);
  return h;
}

/** "Add part": a single body becomes its first part, then a new part comes last. */
export function npAddPart(): void {
  if (!npDraft) return;
  addPart(npDraft, trIn);
  npNode = npDraft.parts!.length - 1;
  renderEditor();
}

/** Import a drawing onto the product or part: a DXF sizes it to its footprint; a
 *  raster keeps its width and takes its aspect ratio. */
async function pickNodeDrawing(holder: DrawingHolder): Promise<void> {
  const res = await platform.importProductFile();
  if (!res.ok) {
    if (res.error && res.error !== 'cancelled') $('np-error').textContent = res.error;
    return;
  }
  try {
    if (res.dxfText) {
      const geom = centerGeometry(parseDxf(res.dxfText));
      holder.geometry = geom;
      delete holder.imageDataUrl;
      holder.dxf = res.dxfText; // keep the source so it's saved in Baserow
      if (isFiniteBounds(geom.bounds)) {
        const size = boundsSize(geom.bounds);
        holder.width = Math.round(size.x);
        holder.depth = Math.round(size.y);
      }
    } else {
      const dataUrl = res.imageDataUrl ?? (res.pdfBase64 ? (await renderPdfToBackground(res.pdfBase64)).dataUrl : undefined);
      if (!dataUrl) { $('np-error').textContent = tr('Could not read the drawing.'); return; }
      holder.imageDataUrl = dataUrl;
      delete holder.geometry;
      delete holder.dxf;
      const sz = await imageSize(dataUrl);
      if (sz.w > 0 && sz.h > 0) holder.depth = Math.max(1, Math.round(holder.width * (sz.h / sz.w)));
    }
    holder.fileName = res.fileName;
    if (res.fileName && holder !== (npDraft as unknown as DrawingHolder) && (!holder.name || /^Part \d+$/.test(holder.name))) {
      holder.name = res.fileName.replace(/\.[^.]+$/, '');
    }
    $('np-error').textContent = '';
    renderEditor();
  } catch (err) {
    $('np-error').textContent = tr('Failed to read drawing') + ': ' + (err as Error).message;
  }
}

/** A catalogue part's drawing picked as polygons on the plan: an armed pick with
 *  no click of its own — Select's marquee takes the polygons (the product modal
 *  is hidden meanwhile). */
interface PolygonPick extends ArmedPick {
  readonly kind: 'part-polygons';
  readonly holder: DrawingHolder;
}
let pickBanner: HTMLElement | null = null;

/** Use the selected polygons as the drawing — or, with none selected, start a marquee pick. */
function usePartPolygons(holder: DrawingHolder): void {
  const selected = (state.drawing.polygons || []).filter((p) =>
    state.selectedPolyIds.includes(p.id) || p.id === state.selectedPolyId);
  if (selected.length) { captureSelectedPolygons(holder, selected); return; }
  startPolygonPick(holder);
}

/** Capture the given polygons as the holder's drawing, sized to them. */
function captureSelectedPolygons(holder: DrawingHolder, polys: Polygon[]): void {
  const ents = polygonsToEntities(polys);
  const geom = centerGeometry({ entities: ents, bounds: computeBounds(ents) });
  holder.geometry = geom;
  delete holder.imageDataUrl;
  delete holder.dxf;
  if (isFiniteBounds(geom.bounds)) {
    const size = boundsSize(geom.bounds);
    holder.width = Math.max(1, Math.round(size.x));
    holder.depth = Math.max(1, Math.round(size.y));
  }
  holder.fileName = `${polys.length} polygon${polys.length > 1 ? 's' : ''}`;
  $('np-error').textContent = '';
  renderEditor();
}

/** Hide the product modal and let the user marquee-select polygons for the holder. */
function startPolygonPick(holder: DrawingHolder): void {
  $('modal-backdrop').classList.add('hidden');
  setTool('select');
  deselectAll();
  const prompt = tr('Drag a marquee over the polygons to use as this part’s symbol · Esc to cancel');
  pickBanner = document.createElement('div');
  pickBanner.textContent = prompt;
  pickBanner.style.cssText = 'position:fixed;top:54px;left:50%;transform:translateX(-50%);z-index:50;background:rgba(20,22,28,0.95);color:#e6eefc;border:1px solid #4aa3ff;border-radius:6px;padding:7px 12px;font-size:13px;pointer-events:none;';
  document.body.appendChild(pickBanner);
  const pick: PolygonPick = { kind: 'part-polygons', holder, prompt: () => prompt, cancel: () => closePolygonPick(holder, false) };
  armPick(pick);
}

/** A marquee has ended: while a part waits for its polygons, the selection it
 *  made becomes the part's drawing and the product modal reopens. */
export function finishPolygonPick(): void {
  const p = armed();
  if (p?.kind !== 'part-polygons') return;
  endArmed();
  closePolygonPick((p as PolygonPick).holder, true);
}

/** End a polygon pick, reopening the product modal. With `capture`, the current
 *  polygon selection becomes the drawing; otherwise nothing changes. */
function closePolygonPick(holder: DrawingHolder, capture: boolean): void {
  if (pickBanner) { pickBanner.remove(); pickBanner = null; }
  if (capture) {
    const polys = (state.drawing.polygons || []).filter((p) => state.selectedPolyIds.includes(p.id) || p.id === state.selectedPolyId);
    if (polys.length) captureSelectedPolygons(holder, polys);
  }
  $('modal-backdrop').classList.remove('hidden');
}

/** Convert drawn polygons (incl. circles/ellipses & holes) to symbol geometry entities. */
function polygonsToEntities(polys: Polygon[]): GeomEntity[] {
  const ents: GeomEntity[] = [];
  // Each polygon's own fill and stroke colours are baked into the entities (a
  // colour-tinted hatch for the fill, colour-tinted outlines for the stroke) so
  // the product symbol keeps the look it was drawn with.
  for (const p of polys) {
    // Bake each polygon's own opacity into the colour (as #rrggbbaa) so the symbol
    // keeps the exact look it was drawn with — an opaque white stays white rather
    // than fading to grey under the default translucent-hatch shading.
    const fillCol = withAlpha(p.fill, p.fillOpacity);
    const strokeCol = withAlpha(p.stroke, p.strokeOpacity);
    if (p.ellipse) {
      const { cx, cy, rx, ry, rot } = ellipseParams(p);
      if (p.fillOpacity > 0) ents.push({ kind: 'hatch', loops: [p.points.map((v) => ({ x: v.x, y: v.y }))], solid: true, color: fillCol });
      ents.push({ kind: 'ellipse', center: { x: cx, y: cy }, major: { x: rx * Math.cos(rot), y: rx * Math.sin(rot) }, ratio: rx ? ry / rx : 1, start: 0, end: Math.PI * 2, closed: true, color: strokeCol });
    } else {
      const outer = p.points.map((v) => ({ x: v.x, y: v.y }));
      const holes = (p.holes || []).map((h) => h.map((v) => ({ x: v.x, y: v.y })));
      // Outer ring plus holes as even-odd loops, so holes stay cut out of the fill.
      if (p.fillOpacity > 0) ents.push({ kind: 'hatch', loops: [outer, ...holes], solid: true, color: fillCol });
      ents.push({ kind: 'polyline', points: outer, closed: true, color: strokeCol });
      for (const h of holes) ents.push({ kind: 'polyline', points: h, closed: true, color: strokeCol });
    }
  }
  return ents;
}

/** Every placed part of one multi-part group, in the drawing's own order. */
function groupMembersOf(inst: ProductInstance): ProductInstance[] {
  const g = productGroupOf(inst);
  return g ? productParts(g.id) : [];
}

/** Open the product editor on ONE placed part. The form is filled from the part
 *  itself (`productOfCopy`), and Save writes back to the drawing only.
 *  A multi-part group is shown whole — the window has no way to display a single
 *  part of a multi-part product — so its Save re-lays that one group. */
export function openProductModalForInstance(inst: ProductInstance): void {
  openProductModal(productTemplateFromParts(inst, groupMembersOf(inst)), inst);
}

/** A product built from placed parts: `inst` alone, or — with 2+ parts in
 *  `group` — a multi-part product of them laid out as placed, relative to
 *  `inst`, with the connections between them. */
function productTemplateFromParts(inst: ProductInstance, group: ProductInstance[]): ProductTemplate {
  const t = productOfCopy(inst);
  if (group.length > 1) {
    // Laid out relative to the part the menu was opened on; `reconcileGroupFromTemplate`
    // recovers the same anchor from it, so nothing already placed moves.
    t.parts = group.map((g, i) => ({
      ...partOfCopy(g),
      id: `p${i}`,
      offset: { x: g.x - inst.x, y: g.y - inst.y },
      rotation: g.rotation - inst.rotation,
    }));
    t.connections = state.drawing.connections
      .filter((c) => group.some((g) => g.id === c.from) && group.some((g) => g.id === c.to))
      .map((c, i) => partConnection(c, `c${i}`, `p${group.findIndex((g) => g.id === c.from)}`, `p${group.findIndex((g) => g.id === c.to)}`));
  }
  return t;
}

/** A plain group of parts whose "Save as product…" is open: linked to the new
 *  catalogue product once it is saved. */
let npLinkGroupId: string | null = null;

/** Right-click ▸ Save as product…: open the product editor on a new catalogue
 *  product made of the group's parts; saving it links the group to it. */
export function saveGroupAsProduct(gid: string): void {
  const parts = productParts(gid);
  if (!parts.length) return;
  const t = productTemplateFromParts(parts[0], parts);
  // Named as the Parts tab showed the group and its parts: a name entered, as
  // typed in every language; else the defaults.
  const g = groupById(state.drawing, gid);
  const source = g?.source ? state.templates.find((x) => x.id === g.source!.templateId) : undefined;
  Object.assign(t, g?.name?.trim() ? typedNames(g.name)
    : source ? { name: source.name, nameI18n: { ...source.nameI18n } }
    : { name: trIn('Group (noun)', 'en'), nameI18n: dictDefaults('Group (noun)', trIn) });
  t.parts?.forEach((q, i) => { const e = parts[i]?.enteredName?.trim(); if (e) Object.assign(q, typedNames(e)); });
  t.id = `user-${Date.now()}`; // a NEW catalogue product, not an edit of the first part's
  openProductModal(t);
  npBase = null;
  $('np-title').textContent = tr('Save as Product');
  $('np-save').textContent = tr('Create Product');
  npLinkGroupId = gid;
}

/** Link a group of parts to the catalogue product just made from it: the group
 *  becomes a placed copy of that product (one undo step). */
function linkGroupToProduct(gid: string, t: ProductTemplate): void {
  const g = groupById(state.drawing, gid);
  if (!g) return;
  pushHistory();
  g.source = { templateId: t.id };
  const parts = productParts(gid);
  for (const p of parts) { p.templateId = t.id; delete p.partId; }
  if (t.parts?.length) reconcileGroupFromTemplate(t, gid, parts, true);
  renderProperties();
  invalidate();
}

/** Write the per-part edit's form onto ONE placed part (catalogueCopy.ts's
 *  'edit': its category, height and own size too). */
function applyTemplateToInstance(inst: ProductInstance, t: ProductTemplate): void {
  refreshCopy(inst, t, 'edit', prepId);
  normalizeCeiling(inst); // a height written below a shaped ceiling's eave is raised to it
}

/** Save the product editor onto the placed part it was opened on. The catalogue
 *  is untouched: nothing here writes it, which is the whole point of the
 *  per-part edit. The drawing's undo takes it back. */
function saveInstanceProduct(): void {
  if (!canEditStandards() || !npDraft) return; // read-only user — Save is hidden; backstop
  const inst = state.drawing.instances.find((i) => i.id === npInstanceId);
  if (!inst) { closeProductModal(); return; }
  const template = deepCopy(npDraft);
  const problem = finishEdit(template);
  if (problem) { $('np-error').textContent = tr(problem); return; }
  pushHistory();
  const group = groupMembersOf(inst);
  const product = productGroupOf(inst);
  if (product && template.parts && template.parts.length && group.length > 1) {
    reconcileGroupFromTemplate(template, product.id, group, false);
    for (const g of productParts(product.id)) postUpdateFixups(g);
    // Changed here only, so it no longer follows the catalogue product: a
    // catalogue update would overwrite the edit. Its parts keep the variant.
    unlinkProductGroup(state.drawing, product.id);
  } else {
    applyTemplateToInstance(inst, template);
    postUpdateFixups(inst);
  }
  closeProductModal();
  syncDimensions();
  renderProperties();
  invalidate();
}

/** Save the product editor to the catalogue: the edited product whole, logged
 *  first, refused over a version someone else saved since it was opened. The
 *  window stays open, with the edit, on any refusal or failure. */
export async function saveNewProduct(): Promise<void> {
  if (npInstanceId) { saveInstanceProduct(); return; }
  if (!canEditStandards() || !npDraft) return; // read-only user — Save is hidden; backstop
  const product = deepCopy(npDraft);
  product.builtin = false;
  const problem = finishEdit(product);
  if (problem) { $('np-error').textContent = tr(problem); return; }
  const unnamed = missingNames(product);
  if (unnamed.length) { $('np-error').textContent = tr('Name the product and its parts in English, Italian and French. Missing: {missing}.').replace('{missing}', unnamed.join('; ')); return; }
  const save = $<HTMLButtonElement>('np-save');
  save.disabled = true;
  const r = await platform.saveProduct(product, npBase);
  save.disabled = false;
  if (!r.ok) {
    $('np-error').textContent = r.conflict
      ? tr('Someone else changed this product since you opened it. Nothing was saved: close this window and open the product again to see their version.')
      : `${tr('Nothing was saved.')} ${r.error}`;
    return;
  }
  const link = npLinkGroupId;
  closeProductModal();
  await loadProducts();
  if (link) linkGroupToProduct(link, product);
}
